import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Grid, Divider, useMediaQuery } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { Image, IconSvg } from 'components/atoms';
import { WebService } from 'services';

const webService = new WebService();

const useStyles = makeStyles((theme) => ({
  root: {
    color: 'rgba(255,255,255,.8)',
    fontFamily: 'Gilroy',
    fontSize: 14,
    background: theme.palette.background.footer,
    backgroundImage: 'url(/assets/images/footer-image.svg)',
    backgroundRepeat: 'no-repeat',
    // backgroundPosition: 'center'
  },
  secoLogo: {
    width: 'calc(100% - 40px)',
    maxWidth: 300,
    height: 'unset',
    marginRight: 20,
  },
  localFinancesLogo: {
    width: 'calc(100% - 40px)',
    maxWidth: 300,
    height: 'unset',
    marginTop: theme.spacing(2),
  },
  footerContainer: {
    maxWidth: theme.layout.contentWidth,
    width: '100%',
    margin: '0 auto',
    marginBottom: theme.spacing(5),
    padding: theme.spacing(0, 2),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(0, 8),
    },
  },
  groupTitle: {
    textTransform: 'uppercase',
    color: theme.palette.primary.dark,
    marginBottom: theme.spacing(1),
  },
  socialIcon: {
    padding: 0,
    marginRight: theme.spacing(1),
    color: 'rgba(255,255,255,.6)',
    '&:hover': {
      background: 'transparent',
    },
    '&:last-child': {
      marginRight: 0,
    },
  },
  footerHeading: {
    marginBottom: theme.spacing(2.25),
    paddingTop: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },
  footerHeadingDivider: {
    borderTop: '1px solid #707070',
    marginBottom: theme.spacing(2.2),
  },
  publicationContainer: {
    marginBottom: theme.spacing(2),
  },
  publicationTitle: {
    fontWeight: 200,
  },
  publicationDate: {
    fontSize: 10,
    marginTop: theme.spacing(0.2),
    fontWeight: 200,
  },
  termAndCondContainer: {
    fontWeight: 200,
    marginBottom: theme.spacing(0.5),
  },
  contactContainer: {
    fontWeight: 200,
    marginBottom: theme.spacing(0.5),
    '& .MuiSvgIcon-fontSizeSmall': {
      fontSize: 14,
    },
  },
  contactIcon: {
    marginRight: theme.spacing(0.5),
    '& .MuiSvgIcon-fontSizeSmall': {
      fontSize: 14,
    },
  },
  copyrightContainer: {
    height: 42,
    backgroundColor: '#000000',
    marginBottom: 0,
    display: 'flex',
    alignItems: 'center',
  },
  copyrightSection: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  copyrightText: {
    fontSize: 10,
  },
  copyrightLogo: {
    width: 25,
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(1),
    },
  },
  copyrightSocialContainer: {
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      flex: 'unset',
      textAlign: 'right',
    },
  },
  copyrightSocialIcon: {
    fontSize: '20px',
    marginRight: theme.spacing(1),
  },
}));

const Footer = (props) => {
  const { pages, className, ...rest } = props;

  const { t } = useTranslation();

  const classes = useStyles();

  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  const [publications, setPublications] = useState([]);

  useEffect(() => {
    webService.footerActivities().then((response) => {
      setPublications(response.data);
    });
  }, []);

  const termsAndConditions = [
    { title: t('HOME_PAGE.PRIVACY_POLICY'), url: '/privacy-policies' },
    { title: t('HOME_PAGE.TERMS_&_CONDITIONS'), url: '/terms-conditions' },
    { title: t('HOME_PAGE.DICTIONARY'), url: '' },
  ];

  const contacts = [
    { title: t('HOME_PAGE.CONTACT_LOCATION'), icon: 'Pin' },
    { title: t('HOME_PAGE.CONTACT_EMAIL'), icon: 'Mail' },
    { title: t('HOME_PAGE.CONTACT_TEL'), icon: 'Phone' },
  ];

  const socialContacts = [
    { title: 'Facebook', icon: 'Facebook', link: '' },
    { title: 'Twitter', icon: 'Twitter', link: '' },
    { title: 'Instagram', icon: 'Instagram', link: '' },
  ];

  return (
    <div {...rest} className={clsx(classes.root, className)}>
      <div className={classes.footerContainer}>
        <Grid container spacing={0}>
          <Grid item xs={12} md={3}>
            <div className={classes.footerHeading}>{t('HOME_PAGE.DEVELOPED_BY')}</div>
            <Divider className={classes.footerHeadingDivider} />
            <Image src={'/assets/images/seco-logo.png'} alt={'SECO'} className={classes.secoLogo} lazy={false} />
            <Image
              src={'/assets/images/financat-locale-logo.png'}
              alt={'Financat Locale Log'}
              className={classes.localFinancesLogo}
              lazy={false}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <div className={classes.footerHeading}>{t('HOME_PAGE.PUBLICATIONS')}</div>
            <Divider className={classes.footerHeadingDivider} />
            {publications.map((pub) => (
              <div key={pub.id} className={classes.publicationContainer}>
                <div className={classes.publicationTitle}>{pub.description}</div>
                <div className={classes.publicationDate}>{pub.date}</div>
              </div>
            ))}
          </Grid>
          <Grid item xs={12} md={3}>
            <div className={classes.footerHeading}>{t('HOME_PAGE.TERMS_&_CONDITIONS')}</div>
            <Divider className={classes.footerHeadingDivider} />
            {termsAndConditions.map((pub) => (
              <div key={pub.title} className={classes.termAndCondContainer}>
                <a href={pub.url} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div>{pub.title}</div>
                </a>
              </div>
            ))}
          </Grid>
          <Grid item xs={12} md={3}>
            <div className={classes.footerHeading}>{t('HOME_PAGE.CONTACT')}</div>
            <Divider className={classes.footerHeadingDivider} />
            {contacts.map((contact) => (
              <div key={contact.title} className={classes.contactContainer}>
                <IconSvg
                  name={`Footer/${contact.icon}`}
                  viewBox='0 -2 16 14'
                  fontSize='small'
                  className={classes.contactIcon}
                />
                {contact.title}
              </div>
            ))}
          </Grid>
        </Grid>
      </div>
      <div className={clsx(classes.copyrightContainer)}>
        {isMd && <div style={{ flex: 1 }}></div>}
        <div className={clsx(classes.copyrightSection)}>
          <Image
            src={'/assets/images/ministry-logo.svg'}
            alt={'Ministria e Financave dhe Ekonomise'}
            className={classes.copyrightLogo}
            lazy={false}
          />
          <div className={classes.copyrightText}>{t('HOME_PAGE.COPYRIGHT')}</div>
        </div>
        <div className={classes.copyrightSocialContainer}>
          {socialContacts.map((social) => (
            <a
              key={social.icon}
              href={social.link}
              // target="_blank"
              // rel="noreferrer"
              title={social.title}
            >
              <IconSvg
                name={`Footer/${social.icon}`}
                viewBox='0 0 20 20'
                fontSize='small'
                className={classes.copyrightSocialIcon}
              />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

Footer.propTypes = {
  className: PropTypes.string,
  pages: PropTypes.object.isRequired,
};

export default Footer;
